import moment from "moment";
import { creationRoles, creationRolesDelimiter } from "./constants";
import {
  Creation,
  CreationRole,
  FuzzySearchResearcherID,
  ResearcherToAddOrEdit,
} from "./types";

function _getSortByLoggedInUserFirst(userID: number) {
  return (a: ResearcherToAddOrEdit, b: ResearcherToAddOrEdit): number => {
    return a._researchers_id.value === userID ? -1 : 1;
  };
}

export function sortByCreationRoles(a: CreationRole, b: CreationRole): number {
  if (!creationRoles.includes(a) && !creationRoles.includes(b)) {
    // Both a and b are custom creation roles (neither are in `creationRoles`)
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    if (aLower === bLower) return 0;
    return aLower < bLower ? -1 : 1;
  }

  // Custom creation roles are moved to the back of the sorted list
  const indexOfA =
    creationRoles.indexOf(a) === -1 ? 999999 : creationRoles.indexOf(a);
  const indexOfB =
    creationRoles.indexOf(b) === -1 ? 999999 : creationRoles.indexOf(b);
  return indexOfA - indexOfB;
}

export function getLabelForFuzzySearchResearcherID(
  researcher: FuzzySearchResearcherID
): string {
  let label = `${researcher.preferred_first_name} ${researcher.preferred_last_name}`;
  if (researcher.departments) {
    label += ` (${researcher.departments})`;
  }
  return label;
}

export function capitalizeFirstLetterOnly(string: string): string {
  // e.g. art director -> Art Director
  return string
    .split(" ")
    .map(
      (token) => token.charAt(0).toUpperCase() + token.toLowerCase().slice(1)
    )
    .join(" ");
}

export function serializeData(data: Creation): void {
  if (data["premiere_year"]) {
    data["premiere_year"] = Number(
      (data["premiere_year"] as moment.Moment).format("YYYY")
    );
  }

  if (data["non_premiere_1_year"]) {
    data["non_premiere_1_year"] = Number(
      (data["non_premiere_1_year"] as moment.Moment).format("YYYY")
    );
  }

  if (data["non_premiere_2_year"]) {
    data["non_premiere_2_year"] = Number(
      (data["non_premiere_2_year"] as moment.Moment).format("YYYY")
    );
  }

  const _duration_minutes: number =
    data["_duration_minutes"] && !isNaN(data["_duration_minutes"])
      ? data["_duration_minutes"]
      : 0;
  const _duration_seconds: number =
    data["_duration_seconds"] && !isNaN(data["_duration_seconds"])
      ? data["_duration_seconds"]
      : 0;
  data["duration_in_seconds"] = 60 * _duration_minutes + _duration_seconds;

  data["researchers"] = data["_researchers_to_add_or_edit"]?.map(
    (researcher) => {
      let roles = "";
      let id = -1;
      if (
        researcher._researchers_roles !== undefined &&
        (researcher._researchers_roles as any) instanceof Array &&
        researcher._researchers_roles.length > 0
      ) {
        roles = (researcher._researchers_roles as CreationRole[])
          .filter(
            (role: CreationRole) =>
              role !== "Other [Specify in text box and press Enter]"
          )
          .map(
            (role: CreationRole) =>
              capitalizeFirstLetterOnly(role) as CreationRole
          )
          .sort(sortByCreationRoles)
          .map((role: CreationRole) =>
            role.replaceAll(creationRolesDelimiter, ",")
          )
          .join(creationRolesDelimiter);
      }
      id = researcher._researchers_id?.value ?? id;

      return {
        roles,
        researcher: id,
      };
    }
  );

  delete data["_duration_minutes"];
  delete data["_duration_seconds"];
  delete data["_researchers_to_add_or_edit"];
}

export function deserializeData(data: Creation, userID: number): Creation {
  const newData: Creation = { ...data };

  if (newData["premiere_year"]) {
    newData["premiere_year"] = moment(newData["premiere_year"], "YYYY");
  }
  if (newData["non_premiere_1_year"]) {
    newData["non_premiere_1_year"] = moment(
      newData["non_premiere_1_year"],
      "YYYY"
    );
  }
  if (newData["non_premiere_2_year"]) {
    newData["non_premiere_2_year"] = moment(
      newData["non_premiere_2_year"],
      "YYYY"
    );
  }

  if (newData["duration_in_seconds"] && newData["duration_in_seconds"] > 0) {
    newData["_duration_minutes"] = Math.floor(
      newData["duration_in_seconds"] / 60
    );
    newData["_duration_seconds"] = newData["duration_in_seconds"] % 60;
  }

  newData["_researchers_to_add_or_edit"] = newData["researchers"]
    ?.map((researcher_with_roles) => {
      const researcher =
        researcher_with_roles.researcher as FuzzySearchResearcherID;
      return {
        _researchers_id: {
          label: getLabelForFuzzySearchResearcherID(researcher),
          value: researcher.id,
        },
        _researchers_roles: researcher_with_roles.roles.split(
          creationRolesDelimiter
        ),
      };
    })
    .sort(_getSortByLoggedInUserFirst(userID));

  delete newData["duration_in_seconds"];
  delete newData["researchers"];

  return newData;
}
